import React, { memo, useCallback } from "react";
import { classNames } from "helpers";

export interface TableGridBodyProps<T extends { id: string }> {
  data: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  selectableItems?: boolean;
  selectedItems?: string[];
  onSelectedChange?: (selected: string[] | string) => void;
}

const TableGridBody = <T extends { id: string }>({
  data,
  renderItem,
  selectableItems = false,
  selectedItems,
  onSelectedChange,
}: TableGridBodyProps<T>) => {
  const handleItemSelect = useCallback(
    (id: string) => {
      if (onSelectedChange) {
        onSelectedChange(id);
      }
    },
    [onSelectedChange]
  );

  return (
    // Broj stupaca prati širinu KONTEJNERA, ne viewporta — bitno jer se sidebar sklapa,
    // pa ista širina ekrana daje različit prostor za mrežu. Minimum od 360px drži
    // kartice čitljivima i ograničava broj stupaca (npr. ~3 na 1280px, ~5 na 1920px).
    // auto-fill (a ne auto-fit) da se jedna kartica ne rastegne preko cijelog reda.
    <div className="grid grid-cols-[repeat(auto-fill,minmax(360px,1fr))] gap-4">
      {data.map((item, index) => {
        const isSelected = selectedItems?.includes(item.id) || false;
        return (
          <div
            key={item.id}
            className={classNames(
              // h-full — kartica se rasteže na visinu reda, pa joj dno (cijene, gumb)
              // ostaje poravnato sa susjedima bez obzira na duljinu sadržaja.
              "relative group h-full [&_img]:transition-[filter] [&_img]:duration-150",
              isSelected && "[&_img]:brightness-[.7]"
            )}
          >
            {selectableItems && (
              // Chip za odabir — prikazuje se na hover cijele kartice ili kad je stavka selektirana.
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleItemSelect(item.id);
                }}
                className={classNames(
                  "absolute top-2 left-2 z-10 inline-flex items-center gap-2 px-3 py-2 rounded-sm border text-sm transition-all duration-150 backdrop-blur-sm",
                  isSelected
                    ? "opacity-100 bg-amber-50/80 border-amber-500 text-amber-700"
                    : "opacity-0 group-hover:opacity-100 bg-white/60 border-slate-200 text-slate-600 hover:border-slate-300"
                )}
              >
                <span
                  className={classNames(
                    "w-4 h-4 rounded-sm border flex items-center justify-center flex-shrink-0",
                    isSelected ? "bg-amber-500 border-amber-500 text-white" : "border-slate-300 bg-white"
                  )}
                >
                  {isSelected && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                Odaberi
              </button>
            )}
            {renderItem(item, index)}
          </div>
        );
      })}
    </div>
  );
};

export default memo(TableGridBody) as typeof TableGridBody;
